import React from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Crop, Check, X, Upload, Circle, Square } from 'lucide-react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

export function AvatarCropDialog({
  open, onCancel, imageSrc, crop, setCrop, onComplete,
  circularCrop, setCircularCrop, onImageLoad, uploading, onSave,
  dragging, fileRef, onDialogDragOver, onDialogDragLeave, onDialogDrop,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md rounded-sm" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Crop className="w-4 h-4" /> Crop Profile Picture
          </DialogTitle>
        </DialogHeader>

        {imageSrc ? (
          <>
            <div className="flex items-center justify-center gap-2 mb-1">
              <button
                onClick={() => setCircularCrop(false)}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-sm font-medium transition-all ${!circularCrop ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid="crop-square-btn"
              >
                <Square className="w-3 h-3" /> Square
              </button>
              <button
                onClick={() => setCircularCrop(true)}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-sm font-medium transition-all ${circularCrop ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid="crop-circle-btn"
              >
                <Circle className="w-3 h-3" /> Circle
              </button>
            </div>

            <div className="flex justify-center bg-slate-50 rounded-sm p-2 min-h-[200px]">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={onComplete}
                aspect={1}
                circularCrop={circularCrop}
                className="max-h-[50vh]"
              >
                <img
                  src={imageSrc}
                  alt="Crop preview"
                  onLoad={onImageLoad}
                  style={{ maxHeight: '50vh', maxWidth: '100%' }}
                  data-testid="crop-image"
                />
              </ReactCrop>
            </div>
            <p className="text-xs text-slate-400 text-center">
              Drag to adjust. {circularCrop ? 'Circle' : 'Square'} crop with 1:1 ratio.
            </p>
          </>
        ) : (
          <div
            onDragOver={onDialogDragOver}
            onDragLeave={onDialogDragLeave}
            onDrop={onDialogDrop}
            onClick={() => fileRef.current?.click()}
            className={`flex flex-col items-center justify-center py-14 border-2 border-dashed rounded-sm cursor-pointer transition-all duration-200 ${
              dragging ? 'border-[#0047AB] bg-blue-50' : 'border-slate-300 hover:border-slate-400 bg-slate-50'
            }`}
            data-testid="drop-zone"
          >
            <Upload className={`w-8 h-8 mb-3 ${dragging ? 'text-[#0047AB]' : 'text-slate-300'}`} />
            <p className="text-sm font-medium text-slate-600">Drag & drop an image here</p>
            <p className="text-xs text-slate-400 mt-1">or click to browse</p>
            <p className="text-[10px] text-slate-300 mt-3">JPEG, PNG, WebP, GIF &middot; Max 5MB</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-sm" data-testid="crop-cancel-btn">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          {imageSrc && (
            <Button onClick={onSave} disabled={uploading} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="crop-save-btn">
              {uploading ? (
                <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-1" /> Uploading...</>
              ) : (
                <><Check className="w-4 h-4 mr-1" /> Save & Upload</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
